/*
 * Screening questions keep their screener types in their screen-out
 * statements. This is the only entry to the screening encodings: pure
 * functions over the rules passed in, never the store, since the reducer
 * pipeline calls them mid-edit.
 */
import { QUESTION_MULTIPLE, QUESTION_SINGLE } from 'data/nodeTypes';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';

import {
  getNodeStatements,
  getScreenOutStatements,
  qlOrderScreeningStatements,
  qlRemoveQuotaStatements,
  qlReplaceScreenOutStatements,
  qlRestorePassOrFailStatements,
  quotaItemOf,
} from './rules';
import {
  decodeSingleMultiple,
  encodeSingleMultiple,
  getOfferedScreenerTypes,
} from './singleMultiple';

export {
  getOfferedScreenerTypes,
  getScreeningErrors,
  normalizeScreeningNavigation,
} from './singleMultiple';
export { getNodeStatements, qlOrderScreeningStatements } from './rules';

const { ALLOWED, STRICT_NOT_ALLOWED } = SCREENING_ALLOWED_VALUES;

// The screener types an item can carry a screening quota on.
export const SCREENER_TYPES_WITH_QUOTA = [ALLOWED];

const isSingleMultiple = (type) =>
  type === QUESTION_SINGLE || type === QUESTION_MULTIPLE;

export const itemKey = ({ answerRefId, columnRefId }) =>
  columnRefId ? `${answerRefId}::${columnRefId}` : answerRefId;

/**
 * `{ [itemKey]: screenerType }` for every item of the node, read from its
 * screen-out statements. Quota and navigation statements are ignored.
 */
export function decodeScreenerTypes({ node, statements }) {
  if (isSingleMultiple(node?.type)) {
    return decodeSingleMultiple({ node, statements });
  }
  return {};
}

function encodeScreenerTypes({ node, screenerTypes, previousStatements }) {
  if (isSingleMultiple(node.type)) {
    return encodeSingleMultiple({ node, screenerTypes, previousStatements });
  }
  return [];
}

/**
 * The quota eligibility predicate for a screening node: true for visible
 * items whose screener type can carry a quota.
 */
export function makeScreeningQuotaEligibility({
  node,
  ql,
  unconditionallyHiddenItems = [],
}) {
  const screenerTypes = decodeScreenerTypes({
    node,
    statements: getNodeStatements(ql, node.refId),
  });
  const isVisible = (refId) =>
    refId === undefined || !unconditionallyHiddenItems.includes(refId);

  return ({ answerRefId, columnRefId }) =>
    isVisible(answerRefId) &&
    isVisible(columnRefId) &&
    SCREENER_TYPES_WITH_QUOTA.includes(
      screenerTypes[itemKey({ answerRefId, columnRefId })],
    );
}

const itemKeysOf = (node) =>
  Object.keys(decodeScreenerTypes({ node, statements: [] }));

const allAllowed = (node) =>
  Object.fromEntries(itemKeysOf(node).map((key) => [key, ALLOWED]));

/**
 * The starting shape of a question added as screening, or reset by a type
 * change: every item allowed.
 */
export function qlSetScreeningStartingShape({ node, ql }) {
  if (!node?.isScreening) return ql;

  return qlReplaceScreenOutStatements({
    ql,
    nodeId: node.refId,
    screenOutStatements: encodeScreenerTypes({
      node,
      screenerTypes: allAllowed(node),
      previousStatements: getScreenOutStatements(ql, node.refId),
    }),
  });
}

// A screener type the question type doesn't offer becomes strict not allowed,
// the closest type that still screens the same respondents out.
const toOffered = (type) => {
  const offered = getOfferedScreenerTypes(type);
  return (screenerType) =>
    offered.includes(screenerType) ? screenerType : STRICT_NOT_ALLOWED;
};

/**
 * Sets the screener types of some of a screening node's items, given as
 * `{ [itemKey]: screenerType }`; the other items keep theirs. A type the
 * question doesn't offer becomes strict not allowed. Only the affected
 * screen-out statements are rewritten, keeping their ids; quota statements are
 * never touched.
 */
export function qlSetScreenerTypes({ node, ql, screenerTypes }) {
  if (!node?.isScreening) return ql;

  const previousStatements = getScreenOutStatements(ql, node.refId);
  const current = decodeScreenerTypes({
    node,
    statements: previousStatements,
  });
  const convert = toOffered(node.type);
  const next = Object.fromEntries(
    Object.entries(current).map(([key, screenerType]) => [
      key,
      convert(screenerTypes[key] || screenerType),
    ]),
  );

  return qlReplaceScreenOutStatements({
    ql,
    nodeId: node.refId,
    screenOutStatements: encodeScreenerTypes({
      node,
      screenerTypes: next,
      previousStatements,
    }),
  });
}

/**
 * The screener types of `source` (`{ [itemKey]: screenerType }`) keyed by the
 * items of `copy`, a duplicate of it: the duplicate keeps its answers in the
 * same order, with new refIds.
 */
export function remapScreenerTypes({ source, copy, screenerTypes = {} }) {
  const copyAnswers = copy.answers || [];
  return Object.fromEntries(
    (source.answers || [])
      .map(({ refId }, index) => [
        copyAnswers[index]?.refId,
        screenerTypes[refId],
      ])
      .filter(([refId, screenerType]) => refId && screenerType),
  );
}

/**
 * Re-encodes a screening node's screen-out statements after an edit: removed
 * items drop out, new items are allowed, and a keepable type change converts
 * the types the new type doesn't offer to strict not allowed. Statement ids
 * are kept; quota statements are never touched.
 *
 * The generic removal handlers drop a removed answer's whole enclosing rule
 * group, so the types are decoded from `previousQl`, the rules as they were
 * before the edit, with the previous node's encoding. It is required: decoding
 * the post-removal `ql` would silently change the other answers' types.
 */
export function qlUpdateScreeningNode({
  node,
  previousNode = node,
  ql,
  previousQl,
}) {
  if (!node?.isScreening) return ql;
  if (!previousQl) {
    throw new Error(
      '[qlUpdateScreeningNode] previousQl (the rules before the edit) is required',
    );
  }

  const previousStatements = getScreenOutStatements(
    previousQl,
    previousNode.refId,
  );
  const previousTypes = decodeScreenerTypes({
    node: previousNode,
    statements: previousStatements,
  });
  const convert = toOffered(node.type);
  const screenerTypes = Object.fromEntries(
    itemKeysOf(node).map((key) => [
      key,
      convert(previousTypes[key] || ALLOWED),
    ]),
  );

  return qlReplaceScreenOutStatements({
    ql,
    nodeId: node.refId,
    screenOutStatements: encodeScreenerTypes({
      node,
      screenerTypes,
      previousStatements,
    }),
  });
}

/**
 * Keeps a screening node's screening rules (screen-out and quota statements)
 * and its navigation statements as they were in `previousQl`: hiding or
 * unhiding an answer goes through the generic answer removal, which would
 * drop the answer's rules and, on multiple selection, the whole combined
 * statement.
 */
export function qlKeepScreeningRules({ node, ql, previousQl }) {
  if (!node?.isScreening) return ql;

  return qlOrderScreeningStatements({
    ql: qlRestorePassOrFailStatements({
      ql,
      previousQl,
      nodeId: node.refId,
    }),
    nodeId: node.refId,
  });
}

/**
 * The items that became eligible for a screening quota with an edit: new
 * items, and items whose screener type now can carry one. An item that was
 * already eligible but has no quota (e.g. a cleared quota, dropped on save)
 * stays without one, so it is still reported missing.
 */
export function makeNewlyEligibleScreeningQuotaItems({
  node,
  previousNode,
  ql,
  previousQl,
  unconditionallyHiddenItems = [],
}) {
  const isEligibleItem = makeScreeningQuotaEligibility({
    node,
    ql,
    unconditionallyHiddenItems,
  });
  const wasEligibleItem = makeScreeningQuotaEligibility({
    node: previousNode,
    ql: previousQl,
    unconditionallyHiddenItems,
  });

  return (item) => isEligibleItem(item) && !wasEligibleItem(item);
}

// The question types a screening quota is offered on.
const SCREENING_QUOTA_TYPES = [QUESTION_SINGLE];

/**
 * Whether the screening node can carry screening quotas at all: an active
 * screener and a type that offers them. Whether enough answers are allowed is
 * the questionnaire's quota-eligibility check, as for answer quotas.
 */
function canCarryScreeningQuotas({ node }) {
  if (!node?.isScreening || node.isScreenerActive === false) return false;
  return SCREENING_QUOTA_TYPES.includes(node.type);
}

/**
 * Keeps a screening node's quota rules in step with its screener types after
 * an edit: an item whose screener type can't carry a quota loses its rule,
 * and every rule goes when the node can't carry quotas at all. Never creates
 * rules; the other rules keep their ids.
 */
export function qlUpdateScreeningQuotas({ node, ql }) {
  if (!node?.isScreening) return ql;

  if (!canCarryScreeningQuotas({ node })) {
    return qlRemoveQuotaStatements({
      ql,
      nodeId: node.refId,
      shouldRemove: () => true,
    });
  }

  const screenerTypes = decodeScreenerTypes({
    node,
    statements: getNodeStatements(ql, node.refId),
  });
  return qlRemoveQuotaStatements({
    ql,
    nodeId: node.refId,
    shouldRemove: (statement) =>
      !SCREENER_TYPES_WITH_QUOTA.includes(
        screenerTypes[itemKey(quotaItemOf(statement))],
      ),
  });
}
